import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ToastService } from '../../../ui';
import { BookedExtensionEntry, ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { SessionService } from '../../../core/session/session.service';
import { extensionDualName } from '../extension-name.util';
import { removableExtensions, SERVICE_24H } from '../extension-availability.util';
import { addDaysIso, todayIso } from '../../../shared/utils/iso-date.util';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmComboboxComponent,
  HlmInputDirective,
  HlmCheckboxComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface RemoveExtensionDialogData {
  contractId: number;
  customerId: number;
  invoiceDay: number;
  currentPlan: string;
  currency: string;
}

/** Everything one reload of the offered set needs; `null` = contract not active on that date. */
type RemovableLoad = {
  booked: BookedExtensionEntry[];
  catalog: { extensionPriceInfo: { name: string } }[];
  names: Record<string, string>;
} | null;

@Component({
  selector: 'cmt-remove-extension-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmComboboxComponent,
    HlmInputDirective, HlmCheckboxComponent, HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideMinus" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading class="pr-8">
          <h2 hlmDialogTitle>Remove Extension</h2>
          <p hlmDialogDescription>
            Contract
            <span class="font-mono-feat">{{ data.contractId }}</span> ·
            <strong>{{ data.currentPlan }}</strong> · {{ data.currency }}
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <!-- The form stays mounted for every state: a date change reloads underneath it (so the
             date field keeps focus between two segment edits) and a load failure is an alert
             above it with a Retry — replacing the body would remove the date field the operator
             needs in order to recover (EB-4209 review run 2). -->
        <form [formGroup]="form" class="flex flex-col gap-4">
          @if (errorMsg()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span class="flex-1">{{ errorMsg() }}</span>
              <button hlmBtn variant="outline" size="sm" type="button" (click)="retry()">
                Retry
              </button>
            </hlm-alert>
          }

          @if (loading()) {
            <div class="flex items-center gap-3 py-1 text-sm text-muted-foreground" role="status">
              <hlm-spinner size="18" />
              <span>Loading extensions...</span>
            </div>
          }

          <hlm-form-field>
            <label hlmLabel for="extension">Extension</label>
            <hlm-combobox
              inputId="extension"
              [options]="extensionOptions()"
              placeholder="Select extension…"
              formControlName="extensionName"
            />
          </hlm-form-field>

          @if (showEmptyNotice()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>There are no extensions available! Please change the date.</span>
            </hlm-alert>
          }

          <hlm-form-field>
            <label hlmLabel for="lastDay">Last day</label>
            <!-- The max bound goes through attr, not the property: a property binding
                 stringifies null into the literal "null", which the browser then ignores
                 as an invalid date. -->
            <input id="lastDay" hlmInput type="date" formControlName="lastDay"
                   [min]="minLastDayStr()" [attr.max]="maxLastDayStr()" />
            @if (form.controls['lastDay'].errors?.['minDate']) {
              <p hlmFieldError>Cannot be before {{ minLastDayStr() }}.</p>
            }
            @if (form.controls['lastDay'].errors?.['maxDate']) {
              <p hlmFieldError>This extension already ends on {{ maxLastDayStr() }}.</p>
            }
          </hlm-form-field>

          @if (isService24h()) {
            <div class="flex flex-col gap-1">
              <hlm-checkbox formControlName="justFromThisContract">
                Remove just from this contract
              </hlm-checkbox>
              <p class="text-sm text-muted-foreground">
                Unchecked, the extension is removed from every contract of this customer.
              </p>
            </div>
          }
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn variant="destructive" (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Removing...
          } @else {
            <hlm-icon name="lucideMinus" size="0.9rem" />
            Remove
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class RemoveExtensionDialogComponent implements OnInit {
  readonly data = inject<RemoveExtensionDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<RemoveExtensionDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly extensions = signal<BookedExtensionEntry[]>([]);
  readonly extensionNames = signal<Record<string, string>>({});
  readonly isService24h = signal(false);
  readonly todayStr = todayIso();
  readonly minLastDayStr = signal(this.todayStr);
  /** The selected extension's own end date, when it already has one; else no upper bound. */
  readonly maxLastDayStr = signal<string | null>(null);

  /** Dates to load the offered set for; only the newest one wins (see `ngOnInit`). */
  private readonly dateChange = new Subject<string>();

  /** Old CMT's "There are no extensions available!" hint — same condition, message copied. */
  readonly showEmptyNotice = computed(
    () => !this.loading() && !this.errorMsg() && this.extensions().length === 0,
  );

  /**
   * Angular's `MinValidator`/`MaxValidator` only bind to `input[type=number]`, so the template's
   * `[min]`/`[max]` on a date input are decoration — without this validator an out-of-range date
   * typed by hand passes `form.valid` and gets submitted (EB-4209 review run 2).
   */
  private readonly lastDayInRange: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string | null;
    if (!value) return null;
    if (value < this.minLastDayStr()) return { minDate: { min: this.minLastDayStr() } };
    const max = this.maxLastDayStr();
    if (max && value > max) return { maxDate: { max } };
    return null;
  };

  readonly form: FormGroup = this.fb.group({
    extensionName: [null as string | null, Validators.required],
    lastDay: [null as string | null, [Validators.required, this.lastDayInRange]],
    // Old CMT leaves this unchecked, i.e. TarifErw2 is removed for the whole customer
    // unless the operator opts into "just this contract" (EB-4209).
    justFromThisContract: [false],
  });

  ngOnInit(): void {
    // switchMap on the date stream, so a slow response for an abandoned date can never
    // overwrite the list of the date the operator is looking at. Note this abandons the
    // *subscription* only — the HTTP calls still complete into the service cache
    // (`shareReplay` with refCount:false), so a repeated date costs no new request but an
    // abandoned one is not aborted either (EB-4209 review run 2).
    this.dateChange
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.errorMsg.set(null);
        }),
        switchMap((dateIso) =>
          this.loadRemovable(dateIso).pipe(catchError(() => of('error' as const))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.applyLoad(result));

    // The offered set depends on the contract revision valid on the last day, so it is
    // re-derived whenever the operator picks another date (old CMT does the same via
    // `updateExtensionsAvailableToDelete`).
    this.form.get('lastDay')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lastDay: string | null) => {
        if (lastDay) this.dateChange.next(lastDay);
      });

    this.form.get('extensionName')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name: string | null) => this.applySelection(this.findExtension(name)));

    this.dateChange.next(this.todayStr);
  }

  /** Re-run the load for the date currently in the form (or today) after a failure. */
  retry(): void {
    const lastDay = (this.form.value.lastDay as string | null) ?? this.todayStr;
    this.errorMsg.set(null);
    this.dateChange.next(lastDay);
  }

  /**
   * Removable = booked extensions still offered by the price list + tariff catalog of the
   * revision valid on `dateIso`, plus TarifErw2 unconditionally (EB-4209). Emits `null` when
   * the contract is not active on that date, which leaves the list empty as in old CMT.
   */
  private loadRemovable(dateIso: string): Observable<RemovableLoad> {
    if (!dateIso) return of(null);
    const brandId = this.sessionService.brandId() ?? 1;
    // Extension names must be resolved in the customer's language, not a hardcoded
    // one — old CMT passes `customer.mainCustomerLanguage` (EB-4143).
    const languageId = this.sessionService.customerLanguage() ?? 1;
    return this.contractsService.getRevisionValidOn(this.data.contractId, dateIso).pipe(
      switchMap((revision) => {
        if (!revision) return of(null);
        return forkJoin({
          booked: this.contractsService.getBookedExtensions(this.data.contractId, dateIso),
          catalog: this.contractsService.getExtensionCatalog(revision.priceListId, revision.tariff),
          names: this.contractsService.getExtensionNames(languageId, brandId).pipe(catchError(() => of({}))),
        });
      }),
    );
  }

  private applyLoad(result: RemovableLoad | 'error'): void {
    if (result === 'error') {
      this.errorMsg.set('Failed to load removable extensions');
      this.loading.set(false);
      return;
    }
    // Read the selection now, not when the request went out — the operator may have picked
    // an extension while it was in flight (EB-4209 review).
    const selectedName = this.form.value.extensionName as string | null;

    if (!result) {
      this.extensions.set([]);
      this.restoreSelection(selectedName);
      this.loading.set(false);
      return;
    }
    const { booked, catalog, names } = result;
    // Keep the labels we already have if the localization call came back empty, otherwise a
    // flaky request would regress every option to its technical name and reorder the list.
    if (Object.keys(names).length > 0) this.extensionNames.set(names);
    // Sort by display name; the options carry the technical name as their value, so the order
    // is presentation only and can never desynchronise from what `submit()` resolves.
    const removable = removableExtensions(booked, catalog).sort((a, b) =>
      this.extLabel(a).localeCompare(this.extLabel(b)),
    );
    this.extensions.set(removable);
    this.restoreSelection(selectedName);
    this.loading.set(false);
  }

  private findExtension(name: string | null): BookedExtensionEntry | null {
    if (!name) return null;
    return this.extensions().find((e) => e.extensionPriceInfo.name === name) ?? null;
  }

  /** Keep the selection across a reload when the same extension is still offered. */
  private restoreSelection(name: string | null): void {
    const ext = this.findExtension(name);
    this.form.get('extensionName')!.setValue(ext ? name : null, { emitEvent: false });
    this.applySelection(ext);
  }

  /**
   * Single writer for everything derived from the selected extension. Called from the control's
   * `valueChanges` AND from `restoreSelection` (which writes with `emitEvent: false`), so the
   * 24h flag and the date bounds can never go stale (EB-4209 review).
   */
  private applySelection(ext: BookedExtensionEntry | null): void {
    this.isService24h.set(ext?.extensionPriceInfo.name === SERVICE_24H);
    // Old CMT caps the last day at the extension's own end date when it has one
    // (`contractRevisions.js:574-580`); with no end date it has no upper bound either.
    this.maxLastDayStr.set(ext?.endDate ? formatDate(ext.endDate, 'yyyy-MM-dd', 'en') : null);
    if (!ext?.startDate) {
      this.minLastDayStr.set(this.todayStr);
    } else {
      // Deliberate deviation: old CMT's effective minimum is `max(start, today) - 1 day`,
      // because `getMinMaxDate(…, true)` subtracts a day on top of the date-picker directive
      // that already subtracts one (`date.picker.field.dir.js:61-71`). That double subtraction
      // is an off-by-one, so the inclusive bound here is `max(start, today)`.
      const start = formatDate(ext.startDate, 'yyyy-MM-dd', 'en');
      this.minLastDayStr.set(start > this.todayStr ? start : this.todayStr);
    }
    this.form.get('lastDay')!.updateValueAndValidity({ emitEvent: false });
  }

  canSubmit(): boolean {
    return this.form.valid && !this.loading() && !this.submitting() && !this.errorMsg();
  }

  /** "Readable (technical)" extension name (EB-4006). */
  extLabel(ext: BookedExtensionEntry): string {
    return extensionDualName(this.extensionNames(), ext.extensionPriceInfo.labelKey, ext.extensionPriceInfo.name);
  }

  /** Options for the searchable select, keyed by technical name: "Readable (technical) — price". */
  readonly extensionOptions = computed(() =>
    this.extensions().map((ext) => ({
      value: ext.extensionPriceInfo.name,
      label: `${this.extLabel(ext)} — ${ext.extensionPriceInfo.monthlyFee} ${this.data.currency}`,
    })),
  );

  submit(): void {
    if (!this.canSubmit()) return;
    const ext = this.findExtension(this.form.value.extensionName as string | null);
    if (!ext) return;
    this.submitting.set(true);

    // API expects endDate = selected last day + 1. Built from the ISO parts, because
    // `new Date('yyyy-MM-dd')` is parsed as UTC and would shift the day in negative offsets.
    const endDate = addDaysIso(this.form.value.lastDay as string, 1);

    // Old CMT sends this on every removal (`deleteFromThisContract` is initialised to false),
    // so the parameter always goes out; the checkbox is only offered for TarifErw2 (EB-4209).
    const justFromThisContract = this.isService24h()
      ? Boolean(this.form.value.justFromThisContract)
      : false;

    // Synchronous current-user read — the canonical pattern hardened in EB-3922
    // (replaces the old user$.subscribe().unsubscribe() anti-pattern).
    this.contractsService.removeExtension(
      ext.extensionPriceInfo.name,
      this.data.contractId,
      endDate,
      String(this.authService.currentUserId),
      justFromThisContract,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toast.open('Extension removed successfully', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Failed to remove extension', 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }
}
